import {
  get,
  includes,
  filter,
  reduce,
  omit,
  map,
  isEmpty,
  isNil,
  set,
  unset,
  isUndefined,
  pick,
  values,
} from 'lodash';
import { hashHistory } from 'react-router';
import * as types from './types';
import * as api from '../utils/recalls-api';
import * as dashboardAPI from '../utils/dashboard-api';
import { getLocale, getSearchSelectedFacetIds, getConfig } from '../selectors';
import {
  CATEGORY_SEARCH_FACETS,
  DEFAULT_LOCALE,
  SEARCH_FACETS,
  UI_FACET_CATEGORY,
  SEARCH_EXCLUSIVE_FACETS,
} from '../utils/constants';

export const onFilterNodeExpand = (filterId, node) => {
  return { type: types.FILTER_NODE_EXPAND, payload: { filterId, node } }
};
export const onFilterNodeCollapse = (filterId, node) => {
  return { type: types.FILTER_NODE_COLLAPSE, payload: { filterId, node } }
};
export const onFacetToggleCollapse = (blockId) => {
  return { type: types.FACET_TOGGLE_COLLAPSE, payload: { blockId } }
};
export const onFacetSpotlightChange = (facetId, change) => {
  return { type: types.FACET_SPOTLIGHT_CHANGE, payload: { facetId, change } };
};

export const onRecallTagSelect = (tag) => (dispatch, getState) => {
  const nextSearch = { ...get(hashHistory.getCurrentLocation(), 'query', {}) };
  nextSearch[SEARCH_FACETS.RELATED_CONCEPTS] = tag.name;
  hashHistory.push({ query: nextSearch });
};

export const changeLocale = (locale) => (dispatch, getState) => {
  const curLocation = hashHistory.getCurrentLocation();
  const nextSearch = { ...get(curLocation, 'query', {}), lang: locale };
  if (locale === DEFAULT_LOCALE) unset(nextSearch, 'lang');
  hashHistory.push({ ...curLocation, query: nextSearch });
};

export const onFacetSelectionChange = (conf, node) => (dispatch, getState) => {
  const allowMultiple = get(conf, 'allowMultiple', false);
  const selectedFacets = getSearchSelectedFacetIds();
  const selectedIds = get(selectedFacets, conf.key, []);
  const isRemove = includes(selectedIds, node.id);

  if (!isRemove && node.disabled) {
    return;
  }

  let nextSelection;
  if (isRemove) {
    nextSelection = filter(selectedIds, x => x !== node.id);
  } else {
    nextSelection = allowMultiple ? [...selectedIds, node.id] : [node.id];
  }

  let nextSearch = { ...get(hashHistory.getCurrentLocation(), 'query', {}) };
  if (conf.key === UI_FACET_CATEGORY) {
    nextSearch = { ...omit(nextSearch, CATEGORY_SEARCH_FACETS), [node.qFacetName]: nextSelection };
  } else if (includes(SEARCH_EXCLUSIVE_FACETS, conf.key)) {
    nextSearch = { ...omit(nextSearch, [...values(SEARCH_FACETS), 'searchTerm']), [conf.key]: nextSelection };
  } else {
    nextSearch[conf.key] = nextSelection;
  }

  unset(nextSearch, 'page');
  hashHistory.push({ query: nextSearch });
};

export const updateRecallsPage = page => (dispatch, getState) => {
  const nextSearch = { ...get(hashHistory.getCurrentLocation(), 'query', {}) };
  isNil(page) || page === 0
    ? unset(nextSearch, 'page')
    : set(nextSearch, 'page', page);
  hashHistory.push({ query: nextSearch });
};

export const updateRecallsSearch = (searchTerm, facetId) => (dispatch, getState) => {
  const nextSearch = { ...get(hashHistory.getCurrentLocation(), 'query', {}) };
  isEmpty(searchTerm)
    ? unset(nextSearch, 'searchTerm')
    : set(nextSearch, 'searchTerm', searchTerm);

  if (!isEmpty(facetId)) {
    nextSearch[SEARCH_FACETS.GPC_SEGMENT] = [facetId];
  } else {
    unset(nextSearch, SEARCH_FACETS.GPC_SEGMENT)
  }

  unset(nextSearch, 'page');
  hashHistory.push({ query: nextSearch });
};

export const updateRecallsSortOpts = (sort, order) => (dispatch, getState) => {
  const nextSearch = { ...get(hashHistory.getCurrentLocation(), 'query', {}) };
  if (!isEmpty(sort)) {
    nextSearch.sort = sort;
  }
  if (!isEmpty(order)) {
    nextSearch.order = order;
  }
  hashHistory.push({ query: nextSearch });
};

export const updateRecallsDates = (dates) => (dispatch, getState) => {
  const nextSearch = { ...get(hashHistory.getCurrentLocation(), 'query', {}) };
  if (!isEmpty(dates)) {
    const [startDate, endDate] = dates;
    nextSearch.startDate = startDate.format('YYYY-MM-DD');
    nextSearch.endDate = endDate.format('YYYY-MM-DD');
  } else {
    unset(nextSearch, 'startDate');
    unset(nextSearch, 'endDate');
  }

  unset(nextSearch, 'page');
  hashHistory.push({ query: nextSearch });
};

export const resetSelection = (all) => (dispatch, getState) => {
  const currentSearch = { ...get(hashHistory.getCurrentLocation(), 'query', {}) };
  let nextSearch = pick(currentSearch, ['page', 'sort', 'order', 'searchTerm', 'lang']);
  if (all) nextSearch = omit(nextSearch, 'searchTerm');
  hashHistory.push({ query: nextSearch });
};

const recall = {
  request: key => action(types.RECALL.REQUEST, { key }),
  success: payload => action(types.RECALL.SUCCESS, { payload }),
  failure: error => action(types.RECALL.FAILURE, { error }),
};

const recalls = {
  request: query => action(types.RECALLS.REQUEST, query),
  success: payload => action(types.RECALLS.SUCCESS, { payload }),
  failure: error => action(types.RECALLS.FAILURE, { error }),
};

const dashboard = {
  request: query => action(types.DASHBOARD.REQUEST, query),
  success: payload => action(types.DASHBOARD.SUCCESS, { payload }),
  failure: error => action(types.DASHBOARD.FAILURE, { error }),
};

export const fetchRecalls = (query) => (dispatch, getState) => {
  const recallsQuery = { ...query, locale: getLocale() };
  const expandedNodeIds = get(getState(), 'recalls.facetExpandedNodesInfo');
  dispatch(recalls.request({ recallsQuery }));
  api
    .fetchRecalls(recallsQuery, getConfig(getState()))
    .then(response => dispatch(recalls.success({ response, query, expandedNodeIds })))
    .catch(error => dispatch(recalls.failure(error)));
};

export const fetchDashboard = (query) => (dispatch, getState) => {
  dispatch(dashboard.request({ query }));
  dashboardAPI
    .getDashboardData(query, getConfig(getState()))
    .then(response => dispatch(dashboard.success({ response, query })))
    .catch(error => dispatch(dashboard.failure(error)));
};

export const updateDashboardYearsRange = (range) => (dispatch, getState) => {
  const nextSearch = { ...get(hashHistory.getCurrentLocation(), 'query', {}) };
  if (!isEmpty(range)) {
    const [fromYear, toYear] = range;
    nextSearch.fromYear = fromYear;
    nextSearch.toYear = toYear;
  } else {
    unset(nextSearch, 'fromYear');
    unset(nextSearch, 'toYear');
  }

  hashHistory.push({ pathname: 'dashboard', query: nextSearch });
};

export const loadRecallDetails = (query) => (dispatch, getState) => {
  dispatch(recall.request(query));
  api
    .fetchRecall(query, getConfig(getState()))
    .then(response => dispatch(recall.success({ response, query })))
    .catch(error => dispatch(recall.failure(error)));
};

function action(type, payload = {}) {
  return { type, ...payload }
}



// WEBPACK FOOTER //
// ./src/actions/index.js